/**
 * FERP Portal - Primitive Relationship Graph
 *
 * Visualizes relationships between the 25 primitives using
 * the 6 proven workflow formulas:
 * - Formula #1: Time-Bound Assignment (Task + Actor + Contract + Notification)
 * - Formula #2: RBAC Approval Chain (Policy + Role + Actor + Approval)
 * - Formula #3: Workflow Dependency Tracking (Workflow + Dependency + Checkpoint + Audit)
 * - Formula #4: Document Approval (Document + Policy + Event + Approval)
 * - Formula #5: Cross-Team Handoff (Task + Queue + Actor + Event)
 * - Formula #6: Load-Balanced Queue (Queue + Actor + Contract + Task)
 */

import { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Title,
  Text,
  Group,
  Badge,
  Stack,
  SegmentedControl,
  Tooltip,
  ActionIcon,
  SimpleGrid,
  Card,
  ThemeIcon,
} from '@mantine/core';
import {
  IconArrowRight,
  IconCircle,
  IconLayoutGrid,
  IconListDetails,
  IconRefresh,
} from '@tabler/icons-react';

import type { PrimitiveType, LifecyclePhase } from './hooks/usePrimitiveData';

// ============================================================================
// TYPES
// ============================================================================

interface Formula {
  id: string;
  name: string;
  description: string;
  successRate: number;
  primitives: PrimitiveType[];
  flow: string[];
}

interface PrimitiveRelationshipGraphProps {
  selectedPrimitive?: PrimitiveType | null;
  onPrimitiveClick?: (type: PrimitiveType) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const LIFECYCLE_PHASES: LifecyclePhase[] = ['DESIGN', 'CONFIGURE', 'EXECUTE', 'MONITOR', 'OPTIMIZE'];

const PHASE_COLORS: Record<LifecyclePhase, string> = {
  DESIGN: 'blue',
  CONFIGURE: 'grape',
  EXECUTE: 'cyan',
  MONITOR: 'orange',
  OPTIMIZE: 'pink',
};

const PRIMITIVES_BY_PHASE: Record<LifecyclePhase, PrimitiveType[]> = {
  DESIGN: ['workflow', 'template', 'dependency', 'policy', 'role'],
  CONFIGURE: ['team', 'actor', 'validation', 'contract', 'constraint'],
  EXECUTE: ['instance', 'task', 'approval', 'document', 'queue'],
  MONITOR: ['event', 'notification', 'trace', 'checkpoint', 'transaction'],
  OPTIMIZE: ['audit', 'analytics', 'schedule', 'resource', 'integration'],
};

// Display names for primitives (for UI)
const PRIMITIVE_DISPLAY_NAMES: Record<PrimitiveType, string> = {
  // DESIGN
  workflow: 'Workflow', template: 'Template', dependency: 'Dependency',
  policy: 'Policy', role: 'Role',
  // CONFIGURE
  team: 'Team', actor: 'Actor', validation: 'Validation',
  contract: 'Contract', constraint: 'Constraint',
  // EXECUTE
  instance: 'Instance', task: 'Task', approval: 'Approval',
  document: 'Document', queue: 'Queue',
  // MONITOR
  event: 'Event', notification: 'Notification', trace: 'Trace',
  checkpoint: 'Checkpoint', transaction: 'Transaction',
  // OPTIMIZE
  audit: 'Audit', analytics: 'Analytics', schedule: 'Schedule',
  resource: 'Resource', integration: 'Integration',
};

const WORKFLOW_FORMULAS: Formula[] = [
  {
    id: 'time-bound-assignment',
    name: 'Time-Bound Assignment',
    description: 'Task assigned to Actor with Contract deadline, Notification on breach',
    successRate: 94,
    primitives: ['task', 'actor', 'contract', 'notification'],
    flow: ['task', 'assigned to', 'actor', 'with deadline from', 'contract', 'alerts via', 'notification'],
  },
  {
    id: 'rbac-approval-chain',
    name: 'RBAC Approval Chain',
    description: 'Policy routes approvals through Role-gated Actors via Approval',
    successRate: 91,
    primitives: ['policy', 'role', 'actor', 'approval'],
    flow: ['policy', 'grants', 'role', 'to', 'actor', 'orchestrated by', 'approval'],
  },
  {
    id: 'workflow-dependency-tracking',
    name: 'Workflow Dependency Tracking',
    description: 'Workflow with Dependencies tracked via Checkpoint and Audit',
    successRate: 88,
    primitives: ['workflow', 'dependency', 'checkpoint', 'audit'],
    flow: ['workflow', 'sequences via', 'dependency', 'marks', 'checkpoint', 'logged by', 'audit'],
  },
  {
    id: 'document-approval',
    name: 'Document Approval',
    description: 'Document reviewed per Policy, changes tracked via Event and Approval',
    successRate: 92,
    primitives: ['document', 'policy', 'event', 'approval'],
    flow: ['document', 'approved by', 'policy', 'emits', 'event', 'signed via', 'approval'],
  },
  {
    id: 'cross-team-handoff',
    name: 'Cross-Team Handoff',
    description: 'Task signals Queue, Event triggers Actor assignment',
    successRate: 89,
    primitives: ['task', 'queue', 'actor', 'event'],
    flow: ['task', 'enters', 'queue', 'signals', 'event', 'assigns', 'actor'],
  },
  {
    id: 'load-balanced-queue',
    name: 'Load-Balanced Queue',
    description: 'Queue distributes Tasks to Actors within Contract SLA',
    successRate: 95,
    primitives: ['queue', 'actor', 'contract', 'task'],
    flow: ['queue', 'distributes', 'task', 'to', 'actor', 'per', 'contract'],
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getPhaseForPrimitive(primitive: PrimitiveType): LifecyclePhase {
  for (const phase of LIFECYCLE_PHASES) {
    if (PRIMITIVES_BY_PHASE[phase].includes(primitive)) {
      return phase;
    }
  }
  return 'DESIGN';
}

function getFormulasForPrimitive(primitive: PrimitiveType): Formula[] {
  return WORKFLOW_FORMULAS.filter(f => f.primitives.includes(primitive));
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PrimitiveRelationshipGraph({
  selectedPrimitive,
  onPrimitiveClick,
}: PrimitiveRelationshipGraphProps) {
  const [viewMode, setViewMode] = useState<'grid' | 'formulas'>('formulas');
  const [highlightedFormula, setHighlightedFormula] = useState<string | null>(null);

  const relevantFormulas = useMemo(() => {
    if (!selectedPrimitive) return WORKFLOW_FORMULAS;
    return getFormulasForPrimitive(selectedPrimitive);
  }, [selectedPrimitive]);

  const highlightedPrimitives = useMemo(() => {
    if (!highlightedFormula) return new Set<PrimitiveType>();
    const formula = WORKFLOW_FORMULAS.find(f => f.id === highlightedFormula);
    return new Set(formula?.primitives ?? []);
  }, [highlightedFormula]);

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <Title order={4}>Primitive Relationships</Title>
          {selectedPrimitive && (
            <Badge variant="light" color={PHASE_COLORS[getPhaseForPrimitive(selectedPrimitive)]}>
              {PRIMITIVE_DISPLAY_NAMES[selectedPrimitive]}
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          <SegmentedControl
            size="xs"
            value={viewMode}
            onChange={(v) => setViewMode(v as 'grid' | 'formulas')}
            data={[
              { value: 'formulas', label: <IconListDetails size={16} /> },
              { value: 'grid', label: <IconLayoutGrid size={16} /> },
            ]}
          />
          <Tooltip label="Reset view">
            <ActionIcon
              variant="subtle"
              onClick={() => {
                setHighlightedFormula(null);
              }}
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {viewMode === 'formulas' ? (
        <Stack gap="sm">
          {relevantFormulas.map((formula) => (
            <Card
              key={formula.id}
              p="sm"
              withBorder
              style={{
                cursor: 'pointer',
                opacity: highlightedFormula && highlightedFormula !== formula.id ? 0.5 : 1,
                transition: 'opacity 0.2s',
              }}
              onClick={() => setHighlightedFormula(
                highlightedFormula === formula.id ? null : formula.id
              )}
            >
              <Group justify="space-between" mb="xs">
                <Text fw={500} size="sm">{formula.name}</Text>
                <Badge size="sm" color="green" variant="light">
                  {formula.successRate}% success
                </Badge>
              </Group>
              <Text size="xs" c="dimmed" mb="sm">{formula.description}</Text>
              <Group gap="xs" wrap="wrap">
                {formula.flow.map((item, idx) => {
                  const isPrimitive = formula.primitives.includes(item as PrimitiveType);
                  const phase = isPrimitive ? getPhaseForPrimitive(item as PrimitiveType) : null;

                  return (
                    <Group key={idx} gap={4}>
                      {isPrimitive ? (
                        <Badge
                          size="sm"
                          color={PHASE_COLORS[phase!]}
                          variant={selectedPrimitive === item ? 'filled' : 'light'}
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onPrimitiveClick?.(item as PrimitiveType);
                          }}
                        >
                          {PRIMITIVE_DISPLAY_NAMES[item as PrimitiveType]}
                        </Badge>
                      ) : (
                        <Text size="xs" c="dimmed" fs="italic">{item}</Text>
                      )}
                      {idx < formula.flow.length - 1 && !isPrimitive && (
                        <IconArrowRight size={12} style={{ color: 'var(--mantine-color-dimmed)' }} />
                      )}
                    </Group>
                  );
                })}
              </Group>
            </Card>
          ))}
          {relevantFormulas.length === 0 && (
            <Text size="sm" c="dimmed" ta="center" py="xl">
              No formulas use this primitive
            </Text>
          )}
        </Stack>
      ) : (
        <Box>
          <SimpleGrid cols={5} spacing="xs">
            {LIFECYCLE_PHASES.map((phase) => (
              <Box key={phase}>
                <Text size="xs" fw={500} c={PHASE_COLORS[phase]} ta="center" mb="xs">
                  {phase}
                </Text>
                <Stack gap={4}>
                  {PRIMITIVES_BY_PHASE[phase].map((primitive) => {
                    const isHighlighted = highlightedPrimitives.has(primitive);
                    const isSelected = selectedPrimitive === primitive;
                    const formulas = getFormulasForPrimitive(primitive);

                    return (
                      <Tooltip
                        key={primitive}
                        label={`${PRIMITIVE_DISPLAY_NAMES[primitive]} - Used in ${formulas.length} formula${formulas.length !== 1 ? 's' : ''}`}
                        position="top"
                      >
                        <Paper
                          p="xs"
                          withBorder
                          style={{
                            cursor: 'pointer',
                            backgroundColor: isSelected
                              ? `var(--mantine-color-${PHASE_COLORS[phase]}-light)`
                              : isHighlighted
                              ? `var(--mantine-color-${PHASE_COLORS[phase]}-0)`
                              : undefined,
                            borderColor: isSelected || isHighlighted
                              ? `var(--mantine-color-${PHASE_COLORS[phase]}-5)`
                              : undefined,
                            opacity: highlightedFormula && !isHighlighted ? 0.4 : 1,
                            transition: 'all 0.2s',
                          }}
                          onClick={() => onPrimitiveClick?.(primitive)}
                        >
                          <Group gap={4} justify="space-between">
                            <Group gap={4}>
                              <ThemeIcon
                                size="xs"
                                color={PHASE_COLORS[phase]}
                                variant={isSelected ? 'filled' : 'light'}
                              >
                                <IconCircle size={8} />
                              </ThemeIcon>
                              <Text size="xs" fw={isSelected ? 600 : 400}>
                                {PRIMITIVE_DISPLAY_NAMES[primitive]}
                              </Text>
                            </Group>
                            {formulas.length > 0 && (
                              <Badge size="xs" variant="dot" color={PHASE_COLORS[phase]}>
                                {formulas.length}
                              </Badge>
                            )}
                          </Group>
                        </Paper>
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      )}
    </Paper>
  );
}

export default PrimitiveRelationshipGraph;
